import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Button, IconButton, Tooltip, Select, MenuItem, LinearProgress, Dialog, DialogTitle, DialogContent, TextField, DialogActions, Stack, FormControl, FormControlLabel, Checkbox, InputLabel } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CancelIcon from '@mui/icons-material/Cancel';
import BackupIcon from '@mui/icons-material/MoneyOff';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import PDFModalDecharge from '../components/PDFModalDecharge';
import { fetchCurrentUser } from '../services/auth';

const DechargeArgentsPage = () => {
  const [dechargeArgents, setDechargeArgents] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [selectedDechargeAuth, setSelectedDechargeAuth] = useState(null);
  const [openPDFModal, setOpenPDFModal] = useState(false);
  const [theUser, setCurrentUser] = useState(null); // 1 for admin, other values for non-admin
  const [loading, setLoading] = useState(true);
  const [openRefuseModal, setOpenRefuseModal] = useState(false);
  const [refuseReason, setRefuseReason] = useState('');
  const [selectedIdToRefuse, setSelectedIdToRefuse] = useState(null);
  const [selectedIdToRemis, setSelectedIdToRemis] = useState(null);
  const [openRemisModal, setOpenRemisModal] = useState(false);
  const [remisValue, setRemisValue] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [editingDecharge, setEditingDecharge] = useState(null);
  const [editForm, setEditForm] = useState({
    employee_id: '',
    reason: '',
    type_decharge: 'Argent',
    somme_argent: '',
    unite_argent: '',
    objet_type: '',
    objet_autre_preciser: '',
    use_cni: false
  });
  const navigate = useNavigate();
  // Date range filter state (uses createdAt)
  const [dateFrom, setDateFrom] = useState(''); // yyyy-mm-dd
  const [dateTo, setDateTo] = useState('');     // yyyy-mm-dd

  const filterOptions = [
    { value: 'all', label: 'Tous' },
    { value: 'validated', label: 'Validés' },
    { value: 'non-validated', label: 'Non Validés' },
    { value: 'refused', label: 'Refusés' },
  ];

  const filteredDechargeArgents = dechargeArgents
    .filter((ea) => {
      if (filterStatus === 'validated') return ea.validated;
      if (filterStatus === 'non-validated') return !ea.validated && !ea.refused;
      if (filterStatus === 'refused') return ea.refused;
      return true;
    })
    .filter((ea) => {
      if (!dateFrom && !dateTo) return true;
      const startRange = dateFrom ? new Date(dateFrom + 'T00:00:00').getTime() : -Infinity;
      const endRange = dateTo ? new Date(dateTo + 'T23:59:59').getTime() : Infinity;
      return ea.createdAtTs >= startRange && ea.createdAtTs <= endRange;
    })
    .filter((ea) => {
      if (!filterEmployee) return true;
      const employeeName = ea.employee ? `${ea.employee.first_name} ${ea.employee.last_name}` : '';
      return employeeName.toLowerCase().includes(filterEmployee.toLowerCase());
    });

  useEffect(() => {
    const loadUser = async () => {
      const currentUser = await fetchCurrentUser();
      if (currentUser) {
        setCurrentUser(currentUser);
      }
      setLoading(false);
    };
    loadUser();
  }, []);

  useEffect(() => {
    const fetchDechargeArgents = async () => {
      try {
        const response = await api.get('/decharge-argents');
        const sortedData = response.data.sort((a, b) => b.id - a.id);
        const formattedData = sortedData.map(ea => {
          const parseTs = (dStr, tStr) => {
            const d = new Date(dStr);
            if (tStr) {
              const [hh = '0', mm = '0'] = String(tStr).split(':');
              d.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);
            }
            return d.getTime();
          };
          const sortieTs = parseTs(ea.sortie_date, ea.sortie_time);
          const entreeTs = parseTs(ea.entree_date, ea.entree_time);
          const createdAtTs = ea.createdAt ? new Date(ea.createdAt).getTime() : NaN;
          const createdAtDateTime = ea.createdAt ? new Date(ea.createdAt).toLocaleString('fr-FR') : '';
          return {
            ...ea,
            employeeName: ea.employee ? `${ea.employee.first_name} ${ea.employee.last_name}` : '............................',
            sortieDateTime: `${new Date(ea.sortie_date).toLocaleDateString()} ${ea.sortie_time}`,
            entreeDateTime: `${new Date(ea.entree_date).toLocaleDateString()} ${ea.entree_time}`,
            sortieTs,
            entreeTs,
            createdAtTs,
            createdAtDateTime,
          };
        });
        setDechargeArgents(formattedData);
      } catch (error) {
        console.error('Error loading decharge argents:', error);
      }
    };
    fetchDechargeArgents();
  }, []);

  const handleExportCSV = () => {
    const rows = filteredDechargeArgents;
    if (!rows || rows.length === 0) {
      alert('Aucune donnée à exporter.');
      return;
    }
    const header = [
      'Employé',
      'Motif',
      'Somme',
      'Monnaie',
      'CNI / Griffe',
      'Date de création',
      'Validé',
      'Refusé',
      'Motif de Refus',
      'Remis',
      'Montant remis'
    ];
    const escapeCsv = (v) => {
      if (v === null || v === undefined) return '';
      // Normalize: replace newlines/tabs with spaces to avoid multi-line CSV rows
      let s = String(v).replace(/[\r\n\t]+/g, ' ').trim();
      if (s.includes('"') || s.includes(',') || s.includes('\n')) {
        s = s.replace(/"/g, '""');
        return '"' + s + '"';
      }
      return s;
    };
    const csv = [header.join(',')]
      .concat(
        rows.map((r) =>
          [
            r.employeeName,
            r.reason || '',
            r.somme_argent ?? '',
            r.unite_argent ?? '',
            r.use_cni ? 'CNI' : 'Griffe',
            r.createdAtDateTime || '',
            r.validated ? 'oui' : 'non',
            r.refused ? 'oui' : 'non',
            r.refuseReason || '',
            r.isRemis ? 'oui' : 'non',
            r.isRemis ? (r.remis ?? '') : '',
          ].map(escapeCsv).join(',')
        )
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const now = new Date();
    const ts = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    link.download = `decharges_argent_${ts}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this decharge argent?')) {
      try {
        await api.delete(`/decharge-argents/${id}`);
        setDechargeArgents(prev => prev.filter(ea => ea.id !== id));
      } catch (error) {
        console.error('Error deleting decharge argent:', error);
      }
    }
  };

  const handleViewPDF = async (authId) => {
    try {
      const response = await api.get(`/decharge-argents/${authId}`);
      const employee = await api.get(`/employees/${response.data.employee_id}`);
      response.data.employee = employee.data;
      setSelectedDechargeAuth(response.data);
      setOpenPDFModal(true);
    } catch (error) {
      console.error('Erreur lors du chargement du PDF:', error);
    }
  };

  const onEditOpen = (decharge) => {
    setEditingDecharge(decharge);
    setEditForm({
      employee_id: decharge.employee_id || '',
      reason: decharge.reason || '',
      type_decharge: decharge.type_decharge || 'Argent',
      somme_argent: decharge.somme_argent || '',
      unite_argent: decharge.unite_argent || '',
      objet_type: decharge.objet_type || '',
      objet_autre_preciser: decharge.objet_autre_preciser || '',
      use_cni: decharge.use_cni !== undefined ? decharge.use_cni : false
    });
    setEditOpen(true);
  };

  const onEditClose = () => {
    setEditOpen(false);
    setEditingDecharge(null);
    setEditForm({
      employee_id: '',
      reason: '',
      type_decharge: 'Argent',
      somme_argent: '',
      unite_argent: '',
      objet_type: '',
      objet_autre_preciser: '',
      use_cni: false
    });
  };

  const onEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm({ 
      ...editForm, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const onEditSubmit = async () => {
    if (!editingDecharge) return;
    try {
      // Préparer les données selon le type de décharge
      const dataToSend = {
        employee_id: editForm.employee_id,
        reason: editForm.reason,
        type_decharge: editForm.type_decharge,
        use_cni: editForm.use_cni
      };

      // Ajouter les champs spécifiques selon le type
      if (editForm.type_decharge === 'Argent') {
        dataToSend.somme_argent = editForm.somme_argent;
        dataToSend.unite_argent = editForm.unite_argent;
        dataToSend.objet_type = null;
        dataToSend.objet_autre_preciser = null;
      } else if (editForm.type_decharge === 'Objet') {
        dataToSend.objet_type = editForm.objet_type;
        dataToSend.objet_autre_preciser = editForm.objet_autre_preciser;
        dataToSend.somme_argent = null;
        dataToSend.unite_argent = null;
      }

      await api.put(`/decharge-argents/${editingDecharge.id}`, dataToSend);
      onEditClose();
      // Reload data
      const response = await api.get('/decharge-argents');
      const formattedData = response.data.map((r) => {
        const createdAtTs = new Date(r.createdAt).getTime();
        return {
          ...r,
          employeeName: r.employee ? `${r.employee.first_name} ${r.employee.last_name}` : '............................',
          createdAtDateTime: new Date(r.createdAt).toLocaleString(),
          createdAtTs,
        };
      });
      setDechargeArgents(formattedData);
    } catch (error) {
      console.error('Error updating decharge argent:', error);
      alert('Erreur lors de la modification de la décharge');
    }
  };

  const handleRefuse = async () => {
    if (!refuseReason.trim()) {
      alert('Please provide a refusal reason.');
      return;
    }

    try {
      await api.post('/decharge-argents/refuse', { id: selectedIdToRefuse, refuseReason });
      setDechargeArgents(prev =>
        prev.map(ea =>
          ea.id === selectedIdToRefuse ? { ...ea, refused: true, refuseReason } : ea
        )
      );
      setOpenRefuseModal(false);
      setRefuseReason('');
    } catch (error) {
      console.error('Error refusing decharge argent:', error);
    }
  };

  const handleRemis = async () => {
    if (!remisValue) {
      alert('Please provide a remis value.');
      return;
    }

    try {
      await api.post('/decharge-argents/remis', { id: selectedIdToRemis, remisValue });
      setDechargeArgents(prev =>
        prev.map(ea =>
          ea.id === selectedIdToRemis ? { ...ea, isRemis: true, remis: remisValue } : ea
        )
      );
      setOpenRemisModal(false);
      setRemisValue(0);
    } catch (error) {
      console.error('Error refusing decharge argent:', error);
    }
  };

  const columns = [
    { field: 'employeeName', headerName: 'Employé', width: 200, headerClassName: 'super-app-theme--header' },
    { field: 'reason', headerName: 'Motif', flex: 1, minWidth: 200, headerClassName: 'super-app-theme--header' },
    { field: 'somme_argent', headerName: 'Somme', width: 130, headerClassName: 'super-app-theme--header', headerAlign: 'right', align: 'right' },
    { field: 'unite_argent', headerName: 'Monnaie', width: 100, headerClassName: 'super-app-theme--header' },
    {
      field: 'use_cni',
      headerName: 'CNI / Griffe',
      width: 120,
      headerClassName: 'super-app-theme--header',
      valueFormatter: (params) => (params ? 'CNI' : 'Griffe'),
    },
    {
      field: 'remis',
      headerName: 'Remis',
      width: 120,
      headerClassName: 'super-app-theme--header',
      headerAlign: 'right',
      align: 'right',
      renderCell: (params) => (params.row.isRemis ? params.row.remis : ''),
    },
    { field: 'refuseReason', headerName: 'Motif de Refus', width: 180, headerClassName: 'super-app-theme--header' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 320,
      sortable: false,
      filterable: false,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const userRole = theUser?.role.toString(); // 1 = admin, 3 = comptable

        return (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', py: 1 }}>
            {!params.row.validated && !params.row.refused && (userRole === "1" || userRole === "3") && (
              <>
                <Tooltip title="Valider">
                  <IconButton
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation(); // Empêche la propagation du clic
                      try {
                        await api.post('/decharge-argents/validate', { id: params.row.id });
                        setDechargeArgents(prev =>
                          prev.map(ea =>
                            ea.id === params.row.id ? { ...ea, validated: true } : ea
                          )
                        );
                      } catch (error) {
                        console.error('Erreur lors de la validation:', error);
                      }
                    }}
                  >
                    <CheckCircleIcon sx={{ color: 'green' }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Refuser">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // Empêche la propagation du clic
                      setSelectedIdToRefuse(params.row.id);
                      setOpenRefuseModal(true);
                    }}
                  >
                    <CancelIcon sx={{ color: 'red' }} />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {params.row.validated && !params.row.isRemis && (userRole === "1" || userRole === "3") && (
              <Tooltip title="Remis">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Empêche la propagation du clic
                    setSelectedIdToRemis(params.row.id);
                    setOpenRemisModal(true);
                  }}
                >
                  <BackupIcon sx={{ color: 'green' }} />
                </IconButton>
              </Tooltip>
            )}

            {params.row.validated && (
              <Tooltip title="Voir PDF">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  handleViewPDF(params.row.id);
                }}>
                  <PictureAsPdfIcon sx={{ color: 'blue' }} />
                </IconButton>
              </Tooltip>
            )}

            {userRole === "1" && (
              <Tooltip title="Supprimer">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  handleDelete(params.row.id);
                }}>
                  <DeleteIcon sx={{ color: 'red' }} />
                </IconButton>
              </Tooltip>
            )}
            {userRole === "1" && (
              <Tooltip title="Modifier">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  onEditOpen(params.row);
                }}>
                  <EditIcon sx={{ color: 'blue' }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <h2>Gestion des decharges</h2>
      <div style={{ display: 'flex', marginBottom: 20 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/create-decharge-argent')}
          style={{ marginRight: 20 }}
        >
          Créer une nouvelle decharge
        </Button>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            variant="outlined"
            style={{ minWidth: 150 }}
          >
            {filterOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <TextField
            label="Employé"
            value={filterEmployee}
            onChange={(e) => setFilterEmployee(e.target.value)}
            placeholder="Filtrer par employé..."
            variant="outlined"
            style={{ minWidth: 200 }}
          />
          <TextField
            label="De"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="Jusqu'à"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Button variant="outlined" onClick={handleExportCSV}>Exporter CSV</Button>
        </div>
      </div>

      <div style={{ height: 600, width: '100%', marginTop: 10 }}>
        <DataGrid
          rows={filteredDechargeArgents}
          columns={columns}
          pageSize={10}
          autoPageSize={false}
          disableSelectionOnClick
          getRowHeight={() => 80}
          getRowClassName={(params) =>
            params.row.validated ? 'validated-row' :
            params.row.refused ? 'refused-row' : 'non-validated-row'
          }
          sx={{
            '& .super-app-theme--header': {
              backgroundColor: '#1976d2',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
            },
            '& .MuiDataGrid-root': {
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#1976d2',
              fontSize: '14px',
              fontWeight: 600,
              color: 'white',
              borderBottom: '2px solid #1565c0',
            },
            '& .MuiDataGrid-columnHeader': {
              '&:focus': {
                outline: 'none',
              },
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            },
            '& .MuiDataGrid-cell': {
              fontSize: '13px',
              padding: '16px 8px',
              borderBottom: '1px solid #f0f0f0',
              '&:focus': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-row': {
              '&:nth-of-type(even)': {
                backgroundColor: '#fafafa',
              },
              '&:hover': {
                backgroundColor: '#e3f2fd',
              },
              '&.Mui-selected': {
                backgroundColor: '#e3f2fd',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#f5f5f5',
            },
            '& .MuiDataGrid-pagination': {
              '& .MuiIconButton-root': {
                borderRadius: '4px',
              },
            },
            '.validated-row': { backgroundColor: '#e8f5e8' },
            '.non-validated-row': { backgroundColor: '#fff8e1' },
            '.refused-row': { backgroundColor: '#ffebee' },
          }}
        />
      </div>

      {openPDFModal && (
        <PDFModalDecharge
          missionData={selectedDechargeAuth}
          onClose={() => setOpenPDFModal(false)}
        />
      )}

      <Dialog open={openRefuseModal} onClose={() => setOpenRefuseModal(false)}>
        <DialogTitle>Refuser decharge</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Motif de Refus"
            fullWidth
            multiline
            rows={3}
            value={refuseReason}
            onChange={(e) => setRefuseReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRefuseModal(false)}>Annuler</Button>
          <Button onClick={handleRefuse} color="error">Refuser</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openRemisModal} onClose={() => setOpenRemisModal(false)}>
        <DialogTitle>Remis d'argent</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            type="number"
            margin="normal"
            label="Somme remise"
            fullWidth
            rows={3}
            value={parseFloat(remisValue).toFixed(2)}
            onChange={(e) => setRemisValue(e.target.value)}
            inputProps={{
              step: '0.01', // Allows input in increments of 0.01
              maxLength: 13, // Ensures the total length doesn't exceed 10 digits + 2 decimals + 1 dot
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRemisModal(false)}>Annuler</Button>
          <Button onClick={handleRemis} color="error">Confirmer</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Decharge Dialog */}
      <Dialog open={editOpen} onClose={onEditClose} maxWidth="md" fullWidth>
        <DialogTitle>Modifier la décharge</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Type de décharge</InputLabel>
            <Select
              name="type_decharge"
              value={editForm.type_decharge}
              onChange={onEditChange}
              label="Type de décharge"
            >
              <MenuItem value="Argent">Décharge d'argent</MenuItem>
              <MenuItem value="Objet">Décharge d'objet</MenuItem>
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            label="Motif"
            name="reason"
            value={editForm.reason}
            onChange={onEditChange}
            fullWidth
            multiline
            rows={3}
          />

          {editForm.type_decharge === 'Argent' ? (
            <>
              <TextField
                margin="dense"
                label="Somme"
                type="number"
                name="somme_argent"
                value={editForm.somme_argent}
                onChange={onEditChange}
                fullWidth
                inputProps={{ step: '0.01' }}
              />
              <TextField
                margin="dense"
                label="Unité monétaire"
                name="unite_argent"
                value={editForm.unite_argent}
                onChange={onEditChange}
                fullWidth
              />
            </>
          ) : (
            <>
              <FormControl fullWidth margin="dense">
                <InputLabel>Type d'objet</InputLabel>
                <Select
                  name="objet_type"
                  value={editForm.objet_type}
                  onChange={onEditChange}
                  label="Type d'objet"
                >
                  <MenuItem value="Telephone">Téléphone</MenuItem>
                  <MenuItem value="ordinateur">Ordinateur</MenuItem>
                  <MenuItem value="chargeur">Chargeur</MenuItem>
                  <MenuItem value="clé">Clé USB/Cle</MenuItem>
                  <MenuItem value="Véhicule">Véhicule</MenuItem>
                  <MenuItem value="Autres">Autres</MenuItem>
                </Select>
              </FormControl>
              
              {editForm.objet_type === 'Autres' && (
                <TextField
                  margin="dense"
                  label="Préciser l'objet"
                  name="objet_autre_preciser"
                  value={editForm.objet_autre_preciser}
                  onChange={onEditChange}
                  fullWidth
                  multiline
                  rows={2}
                />
              )}
            </>
          )}

          <FormControl fullWidth margin="dense">
            <FormControlLabel
              control={
                <Checkbox
                  name="use_cni"
                  checked={editForm.use_cni}
                  onChange={onEditChange}
                />
              }
              label="Utiliser CNI (sinon Griffe)"
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onEditClose}>Annuler</Button>
          <Button onClick={onEditSubmit} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DechargeArgentsPage;